import {
  Article,
  ArticleFooter,
  ArticleImage,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { DiscountedPrice } from "@babackapp/return-widget-components/components/discounted-price";
import type {
  DraftLineItem,
  OrderLineItem,
} from "@babackapp/return-widget-components/types";
import { getLineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import { useLocalizeLineItem } from "@babackapp/return-widget-components/hooks/use-localize-line-item";
import { InfoIcon } from "lucide-react";

type ShopNowReturnedItemProps = {
  item: DraftLineItem;
  orderLineItems: OrderLineItem[];
  currency: string;
  /** Tooltip of the SKU line, shown to a connected dashboard user only. */
  connectedUserTooltip?: string;
};

export function ShopNowReturnedItem({
  item,
  orderLineItems,
  currency,
  connectedUserTooltip,
}: ShopNowReturnedItemProps) {
  const localize = useLocalizeLineItem();
  const display = getLineItemDisplay(
    orderLineItems.map(localize),
    item.lineItemId,
  );

  return (
    <Article>
      <ArticleImage
        src={display.image ?? undefined}
        layoutId={`img-exchange-${item.lineItemId}`}
      />
      <ArticleTitle>{display.title}</ArticleTitle>
      <ArticleSubtitle>
        {[display.subtitle, `×${item.quantity}`].filter(Boolean).join(" — ")}
        {display.price != null && (
          <>
            {" — "}
            <DiscountedPrice
              catalogCents={display.price}
              paidCents={display.paidPrice}
              quantity={item.quantity}
              currency={currency}
            />
          </>
        )}
      </ArticleSubtitle>
      {connectedUserTooltip && display.sku && (
        <ArticleFooter className="flex items-center gap-1.5">
          <span>{display.sku}</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>{connectedUserTooltip}</TooltipContent>
          </Tooltip>
        </ArticleFooter>
      )}
    </Article>
  );
}
